import React, { useEffect, useState } from "react";
import { Label, Radio, RadioGroup } from "@fluentui/react-components";
import {
    GetSinteringTemperatures,
    SinteringTemperatureRow,
} from "../../../services/PowderFormService";
import { usePowder } from "../redux/ReduxContext";
import { FurnaceType } from "../redux/powderOrder";
import CustomSelect from "../CustomSelect";

const formatTemperature = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    return /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed} °C` : trimmed;
};

const buildSinteringDictionary = (
    items: SinteringTemperatureRow[]
): Record<string, string[]> => {
    const dictionary: Record<string, string[]> = {};

    items.forEach((item) => {
        const code = item.Code.trim();
        const temperature = item.Temperature.trim();

        if (!code) {
            return;
        }

        if (!dictionary[code]) {
            dictionary[code] = [];
        }

        if (!dictionary[code].includes(temperature)) {
            dictionary[code].push(temperature);
        }
    });

    return dictionary;
};

export default function OiPowderSintering() {
    const { state, dispatch } = usePowder();
    const [rows, setRows] = useState<SinteringTemperatureRow[]>([]);
    const isProductionFurnace = state.furnaceType === FurnaceType.PRODUCTION;
    const fieldClassName =
        "h-10 w-full border border-gray-300 dark:border-gray-600 bg-primary-light dark:bg-primary-dark px-3 focus:outline-none ";

    const updateTextField = (field: "sinteringCode" | "sinteringTemperature", value: string) => {
        dispatch({
            type: "SET_FIELD",
            payload: { field, value },
        });
    };

    useEffect(() => {
        GetSinteringTemperatures().then((response) => {
            setRows(response || []);
        });
    }, []);

    const sinteringDictionary = buildSinteringDictionary(rows);
    const availableCodes = Object.keys(sinteringDictionary);
    const safeSelectedCode = availableCodes.includes(state.sinteringCode)
        ? state.sinteringCode
        : availableCodes[0] ?? "";
    const availableTemperatures = safeSelectedCode
        ? sinteringDictionary[safeSelectedCode] ?? []
        : [];
    const safeSelectedTemperature = availableTemperatures.includes(state.sinteringTemperature)
        ? state.sinteringTemperature
        : availableTemperatures[0] ?? "";

    useEffect(() => {
        if (!isProductionFurnace || !safeSelectedCode) {
            return;
        }

        if (state.sinteringCode !== safeSelectedCode) {
            updateTextField("sinteringCode", safeSelectedCode);
        }

        if (state.sinteringTemperature !== safeSelectedTemperature) {
            updateTextField("sinteringTemperature", safeSelectedTemperature);
        }
    }, [
        isProductionFurnace,
        safeSelectedCode,
        safeSelectedTemperature,
        state.sinteringCode,
        state.sinteringTemperature,
    ]);

    const handleCodeChange = (value: string) => {
        const temperatures = sinteringDictionary[value] ?? [];
        const nextTemperature = temperatures.includes(state.sinteringTemperature)
            ? state.sinteringTemperature
            : temperatures[0] ?? "";

        updateTextField("sinteringCode", value);
        updateTextField("sinteringTemperature", nextTemperature);
    };

    const handleTemperatureChange = (value: string) => {
        updateTextField("sinteringTemperature", value);
    };

    return (
        <div className="bg-white dark:bg-oi-dark-background p-4 mb-4 drop-shadow-md">
            <div className="mb-2 text-sm font-bold">Sintering</div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-end">
                <div className="lg:col-span-5">
                    <Label className="mb-1 block text-[11px] font-semibold text-primary-dark dark:text-primary-light whitespace-nowrap">
                        Furnace
                    </Label>
                    <RadioGroup
                        layout="horizontal"
                        value={state.furnaceType}
                        onChange={(_, data) =>
                            dispatch({
                                type: "SET_FIELD",
                                payload: {
                                    field: "furnaceType",
                                    value: data.value as FurnaceType,
                                },
                            })
                        }
                    >
                        <Radio value={FurnaceType.PRODUCTION} label="Production furnace" />
                        <Radio value={FurnaceType.LAB} label="Lab furnace" />
                    </RadioGroup>
                </div>

                {/* Sintering fields */}
                <div className="lg:col-span-7">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="mb-1 block text-[11px] font-semibold text-primary-dark dark:text-primary-light whitespace-nowrap">
                                Sintering code
                            </Label>
                            {isProductionFurnace ? (

                                <CustomSelect
                                    className={`h-10 ${fieldClassName}`}
                                    value={safeSelectedCode}
                                    onChange={(e) => handleCodeChange(e.target.value)}
                                >
                                    {availableCodes.map((code) => (
                                        <option
                                            key={code}
                                            value={code}
                                        >
                                            {code}
                                        </option>
                                    ))}
                                </CustomSelect>
                            ) : (
                                <input
                                    type="text"
                                    value={state.sinteringCode}
                                    onChange={(e) =>
                                        updateTextField("sinteringCode", e.target.value)
                                    }
                                    className={`${fieldClassName} px-7 text-xs`}
                                />
                            )}
                        </div>

                        <div>
                            <Label className="mb-1 block text-[11px] font-semibold text-primary-dark dark:text-primary-light whitespace-nowrap">
                                Temperature
                            </Label>
                            {isProductionFurnace ? (
                                <CustomSelect
                                    className={`h-10 ${fieldClassName}`}
                                    value={safeSelectedTemperature}
                                    onChange={(e) => handleTemperatureChange(e.target.value)}
                                >
                                    {availableTemperatures.map((temperature) => (
                                        <option
                                            key={`${safeSelectedCode}-${temperature}`}
                                            value={temperature}
                                        >
                                            {formatTemperature(temperature)}
                                        </option>
                                    ))}
                                </CustomSelect>
                            ) : (

                                <div className="relative ">
                                    <input
                                        className={`${fieldClassName} px-7 text-xs`}
                                        value={state.sinteringTemperature}
                                        onChange={(e) => {
                                            updateTextField("sinteringTemperature", e.target.value)

                                        }}
                                    />
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                                        °C
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};